import React from 'react';
import classes from '../RelatedRooms.module.scss';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { IDocument, } from 'interfaces';
import { CheckOutlined, ClockCircleOutlined, CloseOutlined, EyeOutlined, SignatureFilled, StopOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Popover } from 'antd';
import api from 'api';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setRooms, setSelectedDataRoom, setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { setFoldersForPermissions } from 'store/slices/dataPermissionsSlice';
import { useNavigate } from 'react-router';
import { setIsRSidebarCollapsed, setSelectedKeys } from 'store/slices/windowStateSlice';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { clearReduxFunction, fetchUserPermissions } from 'store/reducers/userDataCreator';
import { setAllTagsDocuments } from 'store/slices/chatsSlice';
import Assests from 'assets/icons';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import { DataNode } from 'antd/es/tree';
import { deleteTermItem, deleteTreeItem, findParentTerm } from '../helpers/updateTreeData';

// @ts-ignore
const isBillingActive = window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE' ? window.REACT_APP_IS_BILLING_ACTIVE : process.env.REACT_APP_IS_BILLING_ACTIVE;

type RelatedRoomTypes = {
  document: IDocument;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTerm: React.Dispatch<React.SetStateAction<IDocument | null>>;
  setRelatedRoomsTerms?: React.Dispatch<React.SetStateAction<IDocument[]>>
  setTreeData?: React.Dispatch<React.SetStateAction<DataNode[]>>;
}

const RelatedRoom = ({ document, setIsOpen, setSelectedTerm, setRelatedRoomsTerms, setTreeData }: RelatedRoomTypes) => {
  const [isDeletingTerm, setIsDeletingTerm] = React.useState(false);
  const [isOpenPopover, setIsOpenPopover] = React.useState(false);

  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userPermissions, available_rooms } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const formatNDA = document.name?.split('.')?.reverse()[0];  
  const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';
  const canEdit = userPermissions?.can_create_access_point;
  const isApproved = document.user_status?.status_access === 'approved';

  const openTermInModal = () => {
    setIsOpen(true);
    setSelectedTerm(document);
  }

  const deleteTerm = async () => {
    setIsDeletingTerm(true);
    try {
      await api.deleteRoomTermOfAccess(document.id);
      setRelatedRoomsTerms && setRelatedRoomsTerms(prev => {        
        const filteredTerms = deleteTermItem(prev, document.id);
        const parentTerm = findParentTerm(prev, document.id);
        
        if (parentTerm?.children?.length === 1) {          
          const parentRoomId = parentTerm?.children && parentTerm?.children[0]?.room?.id;          
          const roomsListModified = available_rooms?.map(someRoom => someRoom.id === parentRoomId
            ? { ...someRoom, has_childs: false }
            : someRoom
          );
          dispatch(setRooms(roomsListModified || []));
        };

        if (!filteredTerms[0]) {
          const roomsListModified = available_rooms?.map(someRoom => someRoom.id === dataRoom?.id
            ? { ...someRoom, has_childs: false }
            : someRoom
          );
          dispatch(setRooms(roomsListModified || []));
        }
        return filteredTerms
      });
      setTreeData && setTreeData(prev => deleteTreeItem(prev, document.id))
      messageApi?.success(t('Settings.terms.successDeleted'));
    } catch (e) {
      messageApi?.error(t('Settings.terms.deleteError'));
    } finally {
      setIsDeletingTerm(false);
    }
  };  
  
  const getStatusRow = (status?: string) => {
    switch(status) {
      case 'denied':
        return <div className={classes.statusTermWrap}>
          <CloseOutlined className={classes.rejectIcon}/>
          <span className={classes.reject}>{t('Settings.terms.state.rejected')}</span>
        </div>;
      case 'approved':
        return <div className={classes.statusTermWrap}>
          <CheckOutlined className={classes.acceptIcon}/>
          <span className={classes.accept}>{t('Settings.terms.state.accepted')}</span>
        </div>;
      default: 
        return <div className={classes.statusTermWrap}>
          <ClockCircleOutlined className={classes.notAcceptIcon}/>
          <span className={classes.nonAccept}>{t('Settings.terms.state.nonAccepted')}</span>
        </div>;
    };
  };

  const navigateToRoom = async () => {
    if (isApproved) {
      const room = available_rooms.find(someRoom => someRoom.id === document?.related_room?.id);

      if (room) {
        let rightOption = room.access_terms_status == 'waiting_for_approve' ? 'nda' : 'documents';
        dispatch(clearReduxFunction({}));
        dispatch(setAllTagsDocuments([]));
        dispatch(setUserAndRoomStatus(''));
        dispatch(setFoldersForPermissions([]));
        dispatch(setSelectedKeys([`${rightOption}`]));
        dispatch(setSelectedDataRoom(room));
        dispatch(setIsRSidebarCollapsed({
          collapsed: true,
          entity: '',
          width: 0
        }));
        navigate(`/room/${room.custom_url || room.id}/${rightOption}`);
  
        // RETURNED BILLING
        if (isBillingActive === 'true') {
          if (checkIsTariffEnded(room)) {
            navigate(`/room/${room?.id}/endedTariff`);
            return;
          } else if (checkIsAwaitingPayment(room)) {        
            navigate(`/room/${room?.id}/awaitingPayment`);
            return;
          }
        }
        await dispatch(fetchRoom({ id: room.id, navigate  }));
        await dispatch(fetchUserPermissions({ idRoom: room.id }));
        dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
      }
    }
  };

  const popoverContent = React.useCallback((term: IDocument) => {
    if (isOpenPopover) {
      if (term.for_all_groups) {
        return t('Settings.relatedRooms.allRolesAvailable');
      } else {
        const rolesNames = term.groups?.map(group => defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name)
        return <span>{t('Settings.relatedRooms.forRolesAvailable')} {rolesNames?.join(', ')}</span>;
      }
    } else {
      return <span></span>
    }
  }, [document, isOpenPopover]);

  const onOpenChange = (open: boolean) => {
    setIsOpenPopover(open)
  }
  
  return (
    <div className={cn(classes.uploadedWrap, canEdit ? classes.canEdit : classes.cantEdit)}>
      {document?.related_room?.room_status === 'available'
        ? <div className={classes.roomName}>
          <div
            className={isApproved ? classes.link : 'null'}
            onClick={navigateToRoom}
          >
            {document?.related_room?.name}
          </div>
          <Popover
            open={isOpenPopover}
            style={{maxWidth: 600}}
            content={popoverContent(document)}
            onOpenChange={onOpenChange}
          >
            <div>
              <Assests.MembersIcon className={classes.membersIcon} />
            </div>
          </Popover>
        </div>
        : <div className={classes.roomNameDeleted}>
          <div className={classes.roomName}>{document?.related_room?.name}</div>
          <div><StopOutlined style={{fontSize: 13, paddingRight: 4}}/>{t('Settings.relatedRooms.roomDeleted')}</div>
        </div>
      }
      <div onClick={openTermInModal} className={classes.card}>
        <div style={{display: 'flex', alignItems:'center'}}>
          <div style={{ fontSize: 32 }}>{documnentIcons[icon as keyof typeof documnentIcons]}</div>
          <div className={classes.docCardName}>{document?.name}</div>
        </div>

        <div
          onClick={openTermInModal}
          className={classes.cardView}
        >
          {document.status === 0 ? <SignatureFilled /> : <EyeOutlined />}
        </div>

      </div>

      {getStatusRow(document.user_status?.status_access)}

      {canEdit && <div className={classes.deleteButton}>
        <Button
          disabled={document.user_status?.status_access !== 'approved'}
          loading={isDeletingTerm}
          danger
          type='text'
          onClick={deleteTerm}
          icon={
            <Assests.TrashIcon
              className={classes.deleteTermIcon}
            />
          }
        />
      </div>}

    </div>
  );
}

export default RelatedRoom;
